import { decodeControlMessage, decodeInputPacket, encodeInputPacket, type ControlMessage, type InputFramePacket } from "./Protocol";
import type { SignalMessage, SignalingClient } from "./SignalingClient";
import { readIceConfiguration, type IceConfiguration } from "./IceConfig";

type ConnectionPhase = "connecting" | "retrying" | "connected" | "failed";

interface PeerConnection {
  id: string;
  connection: RTCPeerConnection;
  inputChannel: RTCDataChannel | null;
  controlChannel: RTCDataChannel | null;
  pendingCandidates: RTCIceCandidateInit[];
  initiator: boolean;
  phase: ConnectionPhase;
  restartAttempts: number;
  retryInFlight: boolean;
  connectionTimer: number | null;
  failureReason?: string;
}

export interface PeerStatus {
  peerId: string;
  state: RTCPeerConnectionState;
  iceState: RTCIceConnectionState;
  phase: ConnectionPhase;
  restartAttempts: number;
  reason?: string;
  ready: boolean;
}

const INPUT_BUFFER_LIMIT = 32_000;
const CONNECTION_TIMEOUT_MS = 7_000;
const MAX_ICE_RESTARTS = 2;

export class WebRTCManager extends EventTarget {
  private readonly connections = new Map<string, PeerConnection>();
  private readonly disconnectTimers = new Map<string, number>();
  private readonly iceServers: RTCIceServer[];
  private readonly inputBacklog: InputFramePacket[] = [];
  private inputConsumer: ((frames: InputFramePacket[]) => void) | null = null;
  readonly turnConfigured: boolean;

  constructor(
    readonly localPeerId: string,
    private readonly signaling: SignalingClient,
    ice: IceConfiguration = readIceConfiguration(),
  ) {
    super();
    this.iceServers = ice.servers;
    this.turnConfigured = ice.turnConfigured;
    signaling.setSignalConsumer((message) => {
      void this.handleSignal(message).catch(() => this.failPeer(message.fromId, "WebRTC negotiation failed."));
    });
  }

  connectToPeers(peerIds: string[]): void {
    for (const peerId of peerIds) {
      if (peerId === this.localPeerId || this.connections.has(peerId)) continue;
      const initiator = this.localPeerId.localeCompare(peerId) < 0;
      const peer = this.createConnection(peerId, initiator);
      if (initiator) void this.createOffer(peer).catch(() => this.retryPeer(peer));
    }
  }

  syncPeers(peerIds: string[]): void {
    const expected = new Set(peerIds);
    for (const [peerId, peer] of this.connections) {
      if (expected.has(peerId)) continue;
      peer.inputChannel?.close();
      peer.controlChannel?.close();
      peer.connection.close();
      this.clearConnectionTimer(peer);
      this.connections.delete(peerId);
    }
    this.connectToPeers(peerIds);
  }

  setInputConsumer(consumer: (frames: InputFramePacket[]) => void): void {
    this.inputConsumer = consumer;
    if (this.inputBacklog.length > 0) consumer(this.inputBacklog.splice(0));
  }

  sendInputs(frames: InputFramePacket[]): void {
    if (frames.length === 0) return;
    const packet = encodeInputPacket(frames);
    for (const peer of this.connections.values()) {
      const channel = peer.inputChannel;
      if (channel?.readyState !== "open" || channel.bufferedAmount > INPUT_BUFFER_LIMIT) continue;
      channel.send(packet);
    }
  }

  broadcastControl(message: ControlMessage): void {
    const payload = JSON.stringify(message);
    for (const peer of this.connections.values()) this.sendControlPayload(peer, payload);
  }

  sendControl(peerId: string, message: ControlMessage): void {
    const peer = this.connections.get(peerId);
    if (peer) this.sendControlPayload(peer, JSON.stringify(message));
  }

  readyPeerIds(): string[] {
    return [...this.connections.values()]
      .filter((peer) => channelsReady(peer))
      .map((peer) => peer.id);
  }

  allReady(expectedRemotePeers: number): boolean {
    return this.connections.size === expectedRemotePeers && this.readyPeerIds().length === expectedRemotePeers;
  }

  retryConnections(): void {
    for (const peer of this.connections.values()) {
      if (channelsReady(peer)) continue;
      peer.restartAttempts = 0;
      peer.failureReason = undefined;
      void this.retryPeer(peer);
    }
  }

  close(): void {
    this.broadcastControl({ type: "peer-leaving", peerId: this.localPeerId });
    for (const peer of this.connections.values()) {
      peer.inputChannel?.close();
      peer.controlChannel?.close();
      peer.connection.close();
      this.clearConnectionTimer(peer);
    }
    this.connections.clear();
  }

  private createConnection(peerId: string, initiator: boolean): PeerConnection {
    const connection = new RTCPeerConnection({ iceServers: this.iceServers, iceCandidatePoolSize: 4 });
    const peer: PeerConnection = {
      id: peerId,
      connection,
      inputChannel: null,
      controlChannel: null,
      pendingCandidates: [],
      initiator,
      phase: "connecting",
      restartAttempts: 0,
      retryInFlight: false,
      connectionTimer: null,
    };
    this.connections.set(peerId, peer);
    connection.onicecandidate = (event) => {
      if (event.candidate) this.signaling.sendSignal(peerId, { candidate: event.candidate.toJSON() });
    };
    connection.onconnectionstatechange = () => {
      this.handleConnectionState(peer);
      if (connection.connectionState === "disconnected" && !this.disconnectTimers.has(peerId)) {
        const timer = window.setTimeout(() => {
          this.disconnectTimers.delete(peerId);
          if (connection.connectionState === "disconnected") this.emitDisconnected(peerId);
        }, 5_000);
        this.disconnectTimers.set(peerId, timer);
      }
    };
    connection.oniceconnectionstatechange = () => this.handleConnectionState(peer);
    connection.ondatachannel = (event) => this.bindChannel(peer, event.channel);
    if (initiator) {
      this.bindChannel(peer, connection.createDataChannel("inputs", { ordered: false, maxRetransmits: 0 }));
      this.bindChannel(peer, connection.createDataChannel("control", { ordered: true }));
    }
    this.scheduleConnectionTimeout(peer);
    this.emitStatus(peer);
    return peer;
  }

  private async createOffer(peer: PeerConnection, iceRestart = false): Promise<void> {
    const offer = await peer.connection.createOffer({ iceRestart });
    await peer.connection.setLocalDescription(offer);
    this.signaling.sendSignal(peer.id, { description: offer });
  }

  private async handleSignal(message: SignalMessage): Promise<void> {
    const peer = this.connections.get(message.fromId) ?? this.createConnection(message.fromId, false);
    if (message.description) {
      await peer.connection.setRemoteDescription(message.description);
      await this.flushCandidates(peer);
      if (message.description.type === "offer") {
        const answer = await peer.connection.createAnswer();
        await peer.connection.setLocalDescription(answer);
        this.signaling.sendSignal(peer.id, { description: answer });
      }
    }
    if (message.candidate) {
      if (peer.connection.remoteDescription) await peer.connection.addIceCandidate(message.candidate);
      else peer.pendingCandidates.push(message.candidate);
    }
  }

  private async flushCandidates(peer: PeerConnection): Promise<void> {
    for (const candidate of peer.pendingCandidates.splice(0)) await peer.connection.addIceCandidate(candidate);
  }

  private bindChannel(peer: PeerConnection, channel: RTCDataChannel): void {
    channel.binaryType = "arraybuffer";
    if (channel.label === "inputs") peer.inputChannel = channel;
    else if (channel.label === "control") peer.controlChannel = channel;
    else return channel.close();

    channel.onopen = () => this.emitStatus(peer);
    channel.onclose = () => this.emitStatus(peer);
    channel.onerror = () => this.emitStatus(peer);
    channel.onmessage = (event) => this.handleChannelMessage(peer.id, channel.label, event.data);
  }

  private handleConnectionState(peer: PeerConnection): void {
    const state = peer.connection.connectionState;
    if (state === "connected") {
      peer.phase = "connected";
      peer.failureReason = undefined;
      this.clearConnectionTimer(peer);
      this.clearDisconnectTimer(peer.id);
    } else if (state === "failed" || peer.connection.iceConnectionState === "failed") {
      void this.retryPeer(peer);
      return;
    } else if (state === "closed") {
      this.failPeer(peer.id, "Peer connection closed.");
      this.emitDisconnected(peer.id);
      return;
    }
    this.emitStatus(peer);
  }

  private async retryPeer(peer: PeerConnection): Promise<void> {
    if (channelsReady(peer) || peer.retryInFlight) return;
    if (peer.restartAttempts >= MAX_ICE_RESTARTS) {
      this.failPeer(peer.id, this.turnConfigured
        ? "TURN failed. Check its URL and credentials."
        : "No direct ICE route. Configure a TURN relay.");
      return;
    }
    peer.retryInFlight = true;
    peer.restartAttempts += 1;
    peer.phase = "retrying";
    peer.failureReason = undefined;
    this.emitStatus(peer);
    try {
      if (peer.initiator) {
        peer.connection.restartIce();
        await this.createOffer(peer, true);
      }
      this.scheduleConnectionTimeout(peer);
    } catch {
      this.failPeer(peer.id, "ICE restart failed.");
    } finally {
      peer.retryInFlight = false;
    }
  }

  private scheduleConnectionTimeout(peer: PeerConnection): void {
    this.clearConnectionTimer(peer);
    peer.connectionTimer = window.setTimeout(() => void this.retryPeer(peer), CONNECTION_TIMEOUT_MS);
  }

  private clearConnectionTimer(peer: PeerConnection): void {
    if (peer.connectionTimer !== null) window.clearTimeout(peer.connectionTimer);
    peer.connectionTimer = null;
  }

  private failPeer(peerId: string, reason: string): void {
    const peer = this.connections.get(peerId);
    if (!peer) return;
    this.clearConnectionTimer(peer);
    peer.phase = "failed";
    peer.failureReason = reason;
    this.emitStatus(peer);
  }

  private handleChannelMessage(peerId: string, channel: string, data: unknown): void {
    if (channel === "inputs" && data instanceof ArrayBuffer) {
      const frames = decodeInputPacket(data).filter((packet) => packet.playerId === peerId);
      if (frames.length > 0 && this.inputConsumer) this.inputConsumer(frames);
      else if (frames.length > 0) {
        this.inputBacklog.push(...frames);
        if (this.inputBacklog.length > 512) this.inputBacklog.splice(0, this.inputBacklog.length - 512);
      }
      return;
    }
    if (channel !== "control" || typeof data !== "string") return;
    const message = decodeControlMessage(data);
    if (message) this.dispatchEvent(new CustomEvent("control", { detail: { peerId, message } }));
  }

  private sendControlPayload(peer: PeerConnection, payload: string): void {
    if (peer.controlChannel?.readyState === "open") peer.controlChannel.send(payload);
  }

  private emitStatus(peer: PeerConnection): void {
    const detail: PeerStatus = {
      peerId: peer.id,
      state: peer.connection.connectionState,
      iceState: peer.connection.iceConnectionState,
      phase: peer.phase,
      restartAttempts: peer.restartAttempts,
      reason: peer.failureReason,
      ready: channelsReady(peer),
    };
    this.dispatchEvent(new CustomEvent<PeerStatus>("status", { detail }));
  }

  private emitDisconnected(peerId: string): void {
    this.clearDisconnectTimer(peerId);
    this.dispatchEvent(new CustomEvent("peer-disconnected", { detail: { peerId } }));
  }

  private clearDisconnectTimer(peerId: string): void {
    const timer = this.disconnectTimers.get(peerId);
    if (timer !== undefined) window.clearTimeout(timer);
    this.disconnectTimers.delete(peerId);
  }
}

const channelsReady = (peer: PeerConnection): boolean => {
  return peer.connection.connectionState === "connected"
    && peer.inputChannel?.readyState === "open"
    && peer.controlChannel?.readyState === "open";
};
